import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { rutaInicio } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { Logo } from "@/components/marca/Logo";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

// Fix Q (2026-10-13): la app del chofer está en inglés, así que esta pantalla se ve en inglés para el chofer
// y sigue en español para administración y despacho.
const TEXTOS = {
  es: {
    get actualVacia() { return t("Escribe tu contraseña actual."); }, get corta() { return t("La contraseña debe tener al menos 6 caracteres."); }, get noCoincide() { return t("Las contraseñas no coinciden."); },
    get ok() { return t("Contraseña actualizada"); }, get titulo() { return t("Cambia tu contraseña"); }, get forzado() { return t("Por seguridad, define una nueva contraseña para continuar."); },
    get normal() { return t("Escribe tu contraseña actual y elige una nueva."); }, get actual() { return t("Contraseña actual"); }, get nueva() { return t("Nueva contraseña"); }, get repetir() { return t("Repetir contraseña"); },
    get guardar() { return t("Guardar y continuar"); }, get cancelar() { return t("Cancelar"); },
  },
};

export default function CambiarPassword() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const tt = TEXTOS.es;
  const forzado = !!user?.debe_cambiar_password;
  const [actual, setActual] = useState("");
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!forzado && actual.length === 0) return setError(tt.actualVacia);
    if (p1.length < 6) return setError(tt.corta);
    if (p1 !== p2) return setError(tt.noCoincide);
    setLoading(true);
    try {
      const payload = { password_nueva: p1 };
      if (!forzado) payload.password_actual = actual;
      await api.post("/auth/cambiar-password", payload);
      await refresh();
      toast.success(tt.ok);
      navigate(rutaInicio({ ...user, debe_cambiar_password: false }), { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-paper p-4">
      <div className="w-full max-w-[420px] bg-white border border-line rounded-xl p-6 sm:p-8">
        <Logo size={30} className="mb-6" />
        <h1 className="font-display font-bold text-[26px] leading-[1.05] text-ink">{tt.titulo}</h1>
        <p className="text-ink-3 mt-1.5 mb-6">
          {forzado ? tt.forzado : tt.normal}
        </p>
        <form onSubmit={submit} className="space-y-4">
          {!forzado && (
            <div>
              <Label>{tt.actual}</Label>
              <Input data-testid="actual-password-input" type="password" value={actual} onChange={(e) => setActual(e.target.value)} className="mt-1.5 h-12" required />
            </div>
          )}
          <div>
            <Label>{tt.nueva}</Label>
            <Input data-testid="nueva-password-input" type="password" value={p1} onChange={(e) => setP1(e.target.value)} className="mt-1.5 h-12" required />
          </div>
          <div>
            <Label>{tt.repetir}</Label>
            <Input data-testid="repetir-password-input" type="password" value={p2} onChange={(e) => setP2(e.target.value)} className="mt-1.5 h-12" required />
          </div>
          {error && <p data-testid="cambiar-error" className="text-sm text-danger font-medium">{error}</p>}
          <Button data-testid="cambiar-submit" type="submit" disabled={loading} className="w-full h-[50px] text-base">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : tt.guardar}
          </Button>
          {!forzado && (
            <Button data-testid="cambiar-cancelar" type="button" variant="ghost" className="w-full h-11" onClick={() => navigate(rutaInicio(user))}>
              {tt.cancelar}
            </Button>
          )}
        </form>
      </div>
    </div>
  );
}
